import { createI18n } from 'vue-i18n';
import { Locale } from 'vant';
import type { App } from 'vue';
import vant_pt_BR from 'vant/es/locale/lang/pt-BR';
import vant_en_US from 'vant/es/locale/lang/en-US';
import vant_my_MM from '@/locales/vant/vant-my-MM'; // 确保路径正确
// import myMMLocale from '@/locales/vant/my-MM.mjs';

const localeModules = import.meta.glob('@/locales/*.{ts,js,mjs}', { eager: false });

const loadLocale = async (code: string) => {
    const map: Record<string, string[]> = {
        'pt-BR': ['pt-br.ts', 'pt-br.js', 'pt-br.mjs'],
        'en-PH': ['en-ph.ts', 'en-ph.js', 'en-ph.mjs'],
        'my-MM': ['my-mm.ts', 'my-mm.js', 'my-mm.mjs'],
    };
    const candidates = map[code] || [];
    for (const name of candidates) {
        const key = `/src/locales/${name}`;
        const mod = (localeModules as any)[key];
        if (mod) {
            const res = await mod();
            return res.default;
        }
    }
    const fallback =
        (localeModules as any)['/src/locales/pt-br.ts'] ||
        (localeModules as any)['/src/locales/pt-br.js'] ||
        (localeModules as any)['/src/locales/pt-br.mjs'];
    if (fallback) {
        const res = await fallback();
        return res.default;
    }
    return {};
};

// 支持的语言配置
export const SUPPORTED_LOCALES = {
    'pt-BR': {
        name: 'Português',
        vantLocale: vant_pt_BR,
        messageLoader: () => loadLocale('pt-BR'),
    },
    'en-PH': {
        name: 'English',
        vantLocale: vant_en_US,
        messageLoader: () => loadLocale('en-PH'),
    },
    'my-MM': {
        name: 'မြန်မာ',
        vantLocale: vant_my_MM,
        messageLoader: () => loadLocale('my-MM'),
    },
} as const;

declare global {
    interface Window {
        language: string;
    }
}

export const DEFAULT_LOCALE: SupportedLocale = (window?.language as SupportedLocale) || 'pt-BR';

export type SupportedLocale = keyof typeof SUPPORTED_LOCALES;

class LanguageManager {
    private i18n: any = null;
    private currentLocale: SupportedLocale = DEFAULT_LOCALE;
    private isInitialized = false;
    private initializationPromise: Promise<void> | null = null;
    private app: App | null = null;

    // 单例模式
    private static instance: LanguageManager;
    public static getInstance(): LanguageManager {
        if (!LanguageManager.instance) {
            LanguageManager.instance = new LanguageManager();
        }
        return LanguageManager.instance;
    }

    /**
     * 从URL参数获取和配置获取 语言设置
     */
    public getLocaleFromConfig(): SupportedLocale | null {
        if (typeof window === 'undefined') return null;

        // 修复：添加类型声明
        const globalWindow = window as any;
        const urlParams = new URLSearchParams(window.location.search);

        // 优先使用url参数,其次使用window.language的配置语言
        const urlLocale =
            urlParams.get('lang') ||
            urlParams.get('language') ||
            urlParams.get('l') ||
            globalWindow.language;

        if (!urlLocale) return null;

        const normalizedLocale = urlLocale.replace('_', '-');

        // 精确匹配
        if (this.isSupportedLocale(normalizedLocale)) {
            return normalizedLocale as SupportedLocale;
        }

        // 前缀匹配
        const langPrefix = normalizedLocale.split('-')[0];
        for (const supportedLocale of Object.keys(SUPPORTED_LOCALES) as SupportedLocale[]) {
            if (supportedLocale.startsWith(langPrefix)) {
                return supportedLocale;
            }
        }

        return null;
    }

    /**
     * 检测系统语言
     */
    public detectSystemLocale(): SupportedLocale {
        return DEFAULT_LOCALE;

        if (typeof navigator === 'undefined') return DEFAULT_LOCALE;

        const systemLang = navigator.language;

        // 精确匹配系统语言
        if (this.isSupportedLocale(systemLang)) {
            return systemLang as SupportedLocale;
        }

        // 前缀匹配系统语言
        const langPrefix = systemLang.split('-')[0];
        for (const supportedLocale of Object.keys(SUPPORTED_LOCALES) as SupportedLocale[]) {
            if (supportedLocale.startsWith(langPrefix)) {
                return supportedLocale;
            }
        }

        return DEFAULT_LOCALE;
    }

    /**
     * 获取最终语言设置（遵循优先级）
     */
    public getFinalLocale(): SupportedLocale {
        // 1. URL参数最高优先级
        const urlLocale = this.getLocaleFromConfig();
        if (urlLocale) return urlLocale;

        // 2. 系统语言检测
        return this.detectSystemLocale();
    }

    /**
     * 检查是否为支持的语言
     */
    public isSupportedLocale(locale: string): locale is SupportedLocale {
        return locale in SUPPORTED_LOCALES;
    }

    /**
     * 获取所有支持的语言列表
     */
    public getSupportedLocales(): { code: SupportedLocale; name: string }[] {
        return Object.entries(SUPPORTED_LOCALES).map(([code, config]) => ({
            code: code as SupportedLocale,
            name: config.name,
        }));
    }

    /**
     * 初始化i18n配置 - 修复版本
     */
    public async initialize(app: App): Promise<void> {
        // 保存app实例
        this.app = app;

        // 防止重复初始化
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        this.initializationPromise = this._initialize();
        return this.initializationPromise;
    }

    private async _initialize(): Promise<void> {
        if (this.isInitialized) return;

        this.currentLocale = this.getFinalLocale();

        console.log('🌐 检测到的语言环境:', {
            urlLocale: this.getLocaleFromConfig(),
            systemLocale: navigator.language,
            finalLocale: this.currentLocale,
        });
        console.log('🌐 支持的语言:', this.getSupportedLocales());

        try {
            const config =
                SUPPORTED_LOCALES[this.currentLocale] || SUPPORTED_LOCALES[DEFAULT_LOCALE];
            const message = await config.messageLoader();

            // 创建i18n实例 - 修复消息配置
            this.i18n = createI18n({
                legacy: false,
                globalInjection: true,
                locale: this.currentLocale,
                fallbackLocale: DEFAULT_LOCALE,
                messages: {
                    [this.currentLocale]: message,
                },
                silentTranslationWarn: false, // 禁用警告，便于调试
                missingWarn: false,
            });
            //注册语言包
            //Locale.add(myMMLocale.vant);
            // 配置Vant组件语言
            Locale.use(this.currentLocale, config.vantLocale);

            // 注册到Vue应用
            if (this.app) {
                this.app.use(this.i18n);
            }

            this.isInitialized = true;

            console.log(`🌐 Language initialized: ${this.currentLocale}`);
            console.log('📦 Available messages:', Object.keys(message));
        } catch (error) {
            console.error('❌ 语言初始化失败:', error);
            throw error;
        }
    }

    /**
     * 获取当前语言
     */
    public getCurrentLocale(isLowerCase: boolean = false): SupportedLocale {
        if (isLowerCase) {
            return this.currentLocale.toLowerCase() as SupportedLocale;
        } else {
            return this.currentLocale;
        }
    }

    /**
     * 获取当前语言名称
     */
    public getCurrentLocaleName(): string {
        const localeInfo = SUPPORTED_LOCALES[this.currentLocale];
        if (!localeInfo) {
            console.warn(`Unsupported locale: ${this.currentLocale}, falling back to default`);
            return SUPPORTED_LOCALES[DEFAULT_LOCALE].name;
        }
        return localeInfo.name;
    }

    /**
     * 切换语言
     */
    public async changeLocale(locale: SupportedLocale): Promise<void> {
        if (!this.isSupportedLocale(locale)) {
            console.warn(`Unsupported locale: ${locale}`);
            return;
        }

        if (locale === this.currentLocale) return;

        try {
            const config = SUPPORTED_LOCALES[locale];

            // 更新i18n - 修复：确保消息已设置
            if (!this.i18n.global.getLocaleMessage(locale)) {
                const message = await config.messageLoader();
                this.i18n.global.setLocaleMessage(locale, message);
            }
            this.i18n.global.locale.value = locale;

            // 更新Vant组件语言
            Locale.use(locale, config.vantLocale);

            this.currentLocale = locale;

            console.log(`🔄 Language changed to: ${locale}`);
        } catch (error) {
            console.error('❌ 语言切换失败:', error);
        }
    }

    /**
     * 获取语言信息
     */
    public getLocaleInfo() {
        const code = this.isSupportedLocale(this.currentLocale)
            ? this.currentLocale
            : DEFAULT_LOCALE;
        return {
            code,
            name: this.getCurrentLocaleName(),
            isPortuguese: code === 'pt-BR',
            isMyanmar: code === 'my-MM', // 添加缅甸语检查
            isPhilippines: code === 'en-PH', // 添加缅甸语检查
            supportedLocales: this.getSupportedLocales(),
            isInitialized: this.isInitialized,
        };
    }

    /**
     * 获取i18n实例（用于直接访问$t等方法）
     */
    public getI18nInstance() {
        return this.i18n;
    }

    /**
     * 生成带语言参数的URL
     */
    public generateURLWithLocale(url: string, locale: SupportedLocale): string {
        const urlObj = new URL(url, window.location.origin);
        urlObj.searchParams.set('lang', locale);
        return urlObj.toString();
    }

    /**
     * 重新检测语言（用于动态更新）
     */
    public async redetectLocale(): Promise<void> {
        const newLocale = this.getFinalLocale();
        if (newLocale !== this.currentLocale) {
            await this.changeLocale(newLocale);
        }
    }

    /**
     * 等待初始化完成
     */
    public async waitForInitialization(): Promise<void> {
        if (this.initializationPromise) {
            await this.initializationPromise;
        }
    }
}

export const languageManager = LanguageManager.getInstance();
export default languageManager;
